import { useMemo } from 'react';
import { useQueries, UseQueryResult } from 'react-query';

import { SaleOrderItemResponseDto } from '@bandido/shared/dtos';

import {
  earliestActiveOperationRecheckBooking,
  useActiveOperationRecheck,
} from '@frontend/features/travel-services/activeOperationRecheck';
import {
  fetchTravelBookingByItem,
  TravelBooking,
  travelBookingByItemQueryKey,
} from '@frontend/features/travel-services/queries';

import {
  classifyRuteroCartAuthorityItems,
  evaluateRuteroCartAuthority,
} from './ruteroCartBookingAuthority.helpers';
import type {
  RuteroCartAuthorityEvaluation,
  RuteroCartBookingAuthorityEntry,
} from './ruteroCartBookingAuthority.helpers';

export {
  classifyRuteroCartAuthorityItems,
  evaluateRuteroCartAuthority,
  formatRuteroCartCommercialDisplay,
  getRuteroCartRowPresentation,
  RUTERO_CART_BOOKING_STATE_COLORS,
} from './ruteroCartBookingAuthority.helpers';
export type {
  RuteroCartAuthorityEvaluation,
  RuteroCartBookingAuthorityEntry,
} from './ruteroCartBookingAuthority.helpers';

export type RuteroCartTravelBookingAuthorityEntry =
  RuteroCartBookingAuthorityEntry<TravelBooking>;

export interface RuteroCartBookingAuthority
  extends RuteroCartAuthorityEvaluation {
  authorityByItemId: Record<string, RuteroCartTravelBookingAuthorityEntry>;
  bookingsByItemId: Record<string, TravelBooking | undefined>;
}

export function useRuteroCartBookingAuthority(
  items: SaleOrderItemResponseDto[],
): RuteroCartBookingAuthority {
  const coverage = useMemo(
    () => classifyRuteroCartAuthorityItems(items),
    [items],
  );
  const itemIds = useMemo(
    () =>
      Array.from(
        new Set(coverage.queryableRuteroItems.map((item) => item.id)),
      ),
    [coverage],
  );

  const queryResults = useQueries(
    itemIds.map((itemId) => ({
      queryKey: travelBookingByItemQueryKey(itemId),
      queryFn: () => fetchTravelBookingByItem(itemId),
      enabled: Boolean(itemId),
      refetchOnWindowFocus: true,
      retry: false,
    })),
  ) as UseQueryResult<TravelBooking>[];

  const entries = useMemo(
    () =>
      itemIds.map((itemId, index) => {
        const result = queryResults[index];
        return {
          itemId,
          booking: result?.data,
          isLoading: Boolean(result?.isLoading && !result?.data),
          isError: Boolean(result?.isError),
        };
      }),
    [itemIds, queryResults],
  );

  const evaluation = useMemo(
    () =>
      evaluateRuteroCartAuthority(entries, {
        hasUnaddressableRuteroItem: coverage.hasUnaddressableRuteroItem,
      }),
    [coverage.hasUnaddressableRuteroItem, entries],
  );

  const activeOperationBooking = useMemo(
    () =>
      earliestActiveOperationRecheckBooking(
        entries
          .map((entry) => entry.booking)
          .filter((booking): booking is TravelBooking => Boolean(booking)),
      ),
    [entries],
  );

  useActiveOperationRecheck(activeOperationBooking);

  return useMemo(() => {
    const authorityByItemId: Record<
      string,
      RuteroCartTravelBookingAuthorityEntry
    > = {};
    const bookingsByItemId: Record<string, TravelBooking | undefined> = {};

    for (const entry of entries) {
      authorityByItemId[entry.itemId] = entry;
      bookingsByItemId[entry.itemId] = entry.booking;
    }

    return {
      ...evaluation,
      authorityByItemId,
      bookingsByItemId,
    };
  }, [entries, evaluation]);
}
